import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { blogAPI, authorsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, FileText, Eye, Calendar, Tag, UserCircle, ExternalLink } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";

const AdminBlog = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [authors, setAuthors] = useState([]);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    content: "",
    excerpt: "",
    featured_image: "",
    meta_title: "",
    meta_description: "",
    canonical_url: "",
    is_published: false,
    tags: "",
    author_id: "",
  });

  useEffect(() => {
    fetchPosts();
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      const res = await authorsAPI.getAll();
      setAuthors(res.data);
    } catch {}
  };

  const fetchPosts = async () => {
    try {
      const response = await blogAPI.getAllAdmin(token);
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to fetch blog posts");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
  };

  const resetForm = () => {
    setForm({
      title: "",
      slug: "",
      content: "",
      excerpt: "",
      featured_image: "",
      meta_title: "",
      meta_description: "",
      canonical_url: "",
      is_published: false,
      tags: "",
      author_id: "",
    });
    setEditingPost(null);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || "",
      featured_image: post.featured_image || "",
      meta_title: post.meta_title || "",
      meta_description: post.meta_description || "",
      canonical_url: post.canonical_url || "",
      is_published: post.is_published,
      tags: (post.tags || []).join(", "),
      author_id: post.author_id || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.error("Please fill in title and content");
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...form,
        slug: form.slug || generateSlug(form.title),
        tags: form.tags.split(",").map(t => t.trim()).filter(t => t),
      };

      if (editingPost) {
        await blogAPI.update(editingPost.id, submitData, token);
        toast.success("Blog post updated");
      } else {
        await blogAPI.create(submitData, token);
        toast.success("Blog post created");
      }

      setShowModal(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save blog post");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog post?")) return;
    try {
      await blogAPI.delete(id, token);
      toast.success("Blog post deleted");
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete blog post");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-blog">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">
              Blog Posts
            </h1>
            <p className="text-muted-foreground">Manage your blog content</p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="rounded-full" data-testid="add-blog-btn">
            <Plus className="w-4 h-4 mr-2" />New Post
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : posts.length > 0 ? (
          <div className="grid gap-4">
            {posts.map((post) => (
              <div
                key={post.id}
                className="bg-white rounded-xl shadow-soft overflow-hidden flex flex-col md:flex-row"
                data-testid={`blog-item-${post.id}`}
              >
                {/* Featured Image */}
                <div className="md:w-48 h-32 md:h-auto bg-muted flex-shrink-0">
                  {post.featured_image ? (
                    <img
                      src={post.featured_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${post.featured_image}` : post.featured_image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-10 h-10 text-muted-foreground/50" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 p-4 flex flex-col">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground text-lg">{post.title}</h3>
                        {post.is_published ? (
                          <Badge className="bg-green-100 text-green-700">Published</Badge>
                        ) : (
                          <Badge variant="secondary">Draft</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">/{post.slug}</p>
                      <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-coconut-green hover:underline flex items-center gap-1 mt-1" data-testid={`view-blog-${post.id}`}><ExternalLink className="w-3 h-3" /> View Live</a>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(post)} data-testid={`edit-blog-${post.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(post.id)} className="text-red-500 hover:bg-red-50" data-testid={`delete-blog-${post.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {post.excerpt && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.excerpt}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-4 mt-auto text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {post.views} views
                    </span>
                    {post.tags && post.tags.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {post.tags.join(", ")}
                      </span>
                    )}
                    {post.author_id && authors.find(a => a.id === post.author_id) && (
                      <span className="flex items-center gap-1 text-primary">
                        <UserCircle className="w-3 h-3" />
                        {authors.find(a => a.id === post.author_id)?.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Blog Posts Yet</h3>
            <p className="text-muted-foreground mb-6">
              Start writing blog posts to share travel tips, destination guides, and more.
            </p>
            <Button onClick={() => { resetForm(); setShowModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />Write First Post
            </Button>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPost ? "Edit Blog Post" : "New Blog Post"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="blog-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Title *</label>
                  <Input
                    value={form.title}
                    onChange={(e) => {
                      setForm({ ...form, title: e.target.value });
                      if (!editingPost) {
                        setForm(f => ({ ...f, slug: generateSlug(e.target.value) }));
                      }
                    }}
                    placeholder="Blog post title"
                    data-testid="form-blog-title"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Slug</label>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="url-friendly-slug"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Content *</label>
                <RichTextEditor
                  value={form.content}
                  onChange={(value) => setForm({ ...form, content: value })}
                  placeholder="Write your blog post content here..."
                  minHeight="250px"
                  token={token}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Excerpt</label>
                <Textarea
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  placeholder="Brief summary for previews (auto-generated if empty)"
                  rows={2}
                />
              </div>

              <ImageUpload
                value={form.featured_image}
                onChange={(url) => setForm({ ...form, featured_image: url })}
                token={token}
                label="Featured Image"
              />

              <div>
                <label className="text-sm font-medium mb-1 block">Tags (comma separated)</label>
                <Input
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="travel, homestay, tips"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Author</label>
                <select
                  value={form.author_id}
                  onChange={(e) => setForm({ ...form, author_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="form-blog-author"
                >
                  <option value="">No author</option>
                  {authors.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold mb-3">SEO Settings</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Meta Title</label>
                    <Input
                      value={form.meta_title}
                      onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                      placeholder="SEO title (defaults to post title)"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Meta Description</label>
                    <Textarea
                      value={form.meta_description}
                      onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                      placeholder="SEO description for search results"
                      rows={2}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium mb-1 block">Canonical URL</label>
                    <Input
                      value={form.canonical_url}
                      onChange={(e) => setForm({ ...form, canonical_url: e.target.value })}
                      placeholder="https://kokanhomestays.in/blog/... (leave empty for auto)"
                    />
                    <p className="text-xs text-muted-foreground mt-0.5">Leave blank to use the default page URL as canonical</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  id="is_published"
                  checked={form.is_published}
                  onCheckedChange={(c) => setForm({ ...form, is_published: c })}
                />
                <label htmlFor="is_published" className="text-sm font-medium cursor-pointer">
                  Publish this post (visible to public)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="form-blog-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingPost ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminBlog;
